import EntityManager from "@/components/EntityManager";
import StatusBadge from "@/components/StatusBadge";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Printer, FileSpreadsheet, MessageCircle, CheckCircle2 } from "lucide-react";
import { inr } from "@/lib/format";
import { fmtDate, today } from "@/lib/format";
import { printSalarySlip, printSalaryRegister, printSalaryChequeRegister } from "@/lib/print";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

const shareSlipWhatsApp = (r, masters) => {
  const key = String(r.employee_name || "").trim().toUpperCase();
  const staff = (masters || []).find((m) => String(m.name || "").trim().toUpperCase() === key);
  const phone = staff?.phone;
  if (!phone) {
    toast.error(`No phone saved for ${r.employee_name} in Staff Master.`);
    return;
  }
  const d = String(phone).replace(/\D/g, "");
  const msg = [
    "*NISHA TRANSPORT CORPORATION*",
    `Salary Slip — ${r.month || ""}`,
    "",
    `Employee: ${r.employee_name || ""}${r.designation ? ` (${r.designation})` : ""}${r.office ? ` · ${r.office} Branch` : ""}`,
    r.employee_code ? `Employee Code: ${r.employee_code}` : "",
    r.payable_days ? `Payable Days: ${r.payable_days}` : "",
    `Basic: ${inr(r.basic)}`,
    Number(r.allowances) > 0 ? `Allowances: +${inr(r.allowances)}` : "",
    Number(r.deductions) > 0 ? `Deductions: -${inr(r.deductions)}` : "",
    Number(r.pf) > 0 ? `PF: -${inr(r.pf)}` : "",
    Number(r.esi) > 0 ? `ESI: -${inr(r.esi)}` : "",
    Number(r.loan) > 0 ? `Loan: -${inr(r.loan)}` : "",
    `*NET PAY: ${inr(r.net_pay)}*`,
    r.payment_mode === "Cheque" ? `Cheque No. ${r.cheque_no || "—"}${r.cheque_date ? ` dt ${fmtDate(r.cheque_date)}` : ""}${Number(r.cheque_amount) ? ` · ${inr(r.cheque_amount)}` : ""}${r.bank_name ? ` · ${r.bank_name}` : ""}` : "",
    r.payment_mode === "Bank" || r.bank_name || r.account_no ? `Bank: ${r.bank_name || "—"}${r.account_no ? ` · A/c ${r.account_no}` : ""}` : "",
    r.status === "Paid" ? `Status: Paid${r.paid_date ? ` on ${fmtDate(r.paid_date)}` : ""}${r.payment_mode ? ` (${r.payment_mode})` : ""}` : "Status: Pending",
    "",
    "This is a system-generated salary summary.",
  ].filter(Boolean).join("\n");
  window.open(`https://wa.me/${d.length === 10 ? "91" + d : d}?text=${encodeURIComponent(msg)}`, "_blank");
};

function SalaryChequeRegister({ rows, month }) {
  const [open, setOpen] = useState(false);
  const [local, setLocal] = useState({});
  const list = (rows || [])
    .filter((r) => r.payment_mode === "Cheque" && (month === "All" || r.month === month))
    .map((r) => ({ ...r, cheque_status: local[r.id] ?? r.cheque_status ?? "Pending" }))
    .sort((a, b) => String(a.cheque_date || "").localeCompare(String(b.cheque_date || "")));
  const amt = (r) => Number(r.cheque_amount) || Number(r.net_pay) || 0;
  const setStatus = async (r, s) => {
    try {
      await api.put(`/salaries/${r.id}/cheque-status`, { status: s });
      setLocal((p) => ({ ...p, [r.id]: s }));
      toast.success(`Cheque ${r.cheque_no || ""} marked ${s}`);
    } catch {
      toast.error("Failed to update cheque status");
    }
  };
  const stColor = { Cleared: "text-emerald-700", Bounced: "text-red-700", Pending: "text-amber-700" };
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} disabled={!rows.length} data-testid="print-cheque-register-btn">
        <FileSpreadsheet className="mr-2 h-4 w-4" /> Cheque Register
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] max-w-5xl overflow-y-auto" data-testid="salary-cheque-register-dialog">
          <DialogHeader>
            <DialogTitle>Salary Cheque Register {month !== "All" ? `— ${month}` : ""}</DialogTitle>
          </DialogHeader>
          {list.length ? (
            <>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>{list.length} cheque(s) · Total {inr(list.reduce((a, r) => a + amt(r), 0))} — mark each cheque Cleared or Bounced against the bank statement.</span>
                <Button size="sm" variant="outline" onClick={() => printSalaryChequeRegister(list, month)} data-testid="salary-cheque-print-btn">
                  <Printer className="mr-2 h-4 w-4" /> Print
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                      <th className="py-2 pr-2">Cheque No.</th><th className="pr-2">Date</th><th className="pr-2">Bank</th><th className="pr-2">Employee</th><th className="pr-2">Branch</th><th className="pr-2">Month</th><th className="pr-2 text-right">Amount</th><th>Clearing Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((r) => (
                      <tr key={r.id} className="border-b" data-testid="salary-cheque-row">
                        <td className="py-2 pr-2 font-semibold">{r.cheque_no || "—"}</td>
                        <td className="pr-2 whitespace-nowrap">{r.cheque_date ? fmtDate(r.cheque_date) : "—"}</td>
                        <td className="pr-2">{r.bank_name || "—"}</td>
                        <td className="pr-2">{r.employee_name || "—"}</td>
                        <td className="pr-2">{r.office || "—"}</td>
                        <td className="pr-2">{r.month || "—"}</td>
                        <td className="pr-2 text-right font-semibold">{inr(amt(r))}</td>
                        <td>
                          <Select value={r.cheque_status} onValueChange={(s) => setStatus(r, s)}>
                            <SelectTrigger className={`h-8 w-[120px] text-xs font-semibold ${stColor[r.cheque_status]}`} data-testid="salary-cheque-status-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Pending">Pending</SelectItem>
                              <SelectItem value="Cleared">Cleared</SelectItem>
                              <SelectItem value="Bounced">Bounced</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="py-6 text-center text-sm text-muted-foreground" data-testid="salary-cheque-empty">No cheque-paid salaries found{month !== "All" ? ` for ${month}` : ""}.</div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function SalaryRegister({ rows }) {
  const months = Array.from(new Set((rows || []).map((r) => r.month).filter(Boolean))).sort().reverse();
  const [month, setMonth] = useState("All");
  const exportExcel = () => {
    const list = (rows || []).filter((s) => month === "All" || s.month === month);
    if (!list.length) return;
    const cell = (v) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const net = (s) => s.net_pay ?? (Number(s.basic) || 0) + (Number(s.allowances) || 0) - (Number(s.deductions) || 0) - (Number(s.pf) || 0) - (Number(s.esi) || 0);
    const lines = ["Sl,Ref No.,Month,Employee,Employee Code,Designation,Branch,Days,Basic,Allowances,Deductions,PF,ESI,Loan,Net Pay,Payment Mode,Account No.,Bank Name,IFSC Code,Bank Address,Status"];
    list.forEach((s, i) =>
      lines.push([i + 1, String(s.salary_no || "").replace(/^SAL[-\s]?/i, ""), s.month || "", s.employee_name || "", s.employee_code || "", s.designation || "", s.office || "", s.payable_days || "", s.basic || 0, s.allowances || 0, s.deductions || 0, s.pf || 0, s.esi || 0, s.loan || 0, net(s), s.payment_mode || "", s.account_no || "", s.bank_name || "", s.ifsc_code || "", s.bank_address || "", s.status || ""].map(cell).join(","))
    );
    const url = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `salary-register-${month === "All" ? "all-months" : month}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="flex items-center gap-2">
      <Select value={month} onValueChange={setMonth}>
        <SelectTrigger className="w-[150px]" data-testid="register-month"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="All">All months</SelectItem>
          {months.map((m) => (
            <SelectItem key={m} value={m}>{m}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button
        variant="outline"
        onClick={exportExcel}
        disabled={!rows.length}
        data-testid="export-salary-register-btn"
      >
        <FileSpreadsheet className="mr-2 h-4 w-4" /> Excel
      </Button>
      <Button
        variant="outline"
        onClick={() => rows.length && printSalaryRegister(rows, month)}
        disabled={!rows.length}
        data-testid="print-salary-register-btn"
      >
        <FileSpreadsheet className="mr-2 h-4 w-4" /> Print Register
      </Button>
      <SalaryChequeRegister rows={rows} month={month} />
    </div>
  );
}

function BulkSlipSender({ rows, masters }) {
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [branch, setBranch] = useState("All");
  const [idx, setIdx] = useState(0);
  const [done, setDone] = useState(() => new Set());
  const months = Array.from(new Set((rows || []).map((r) => r.month).filter(Boolean))).sort().reverse();
  const list = (rows || [])
    .filter((r) => r.month === month && (branch === "All" || (r.office || "") === branch))
    .sort((a, b) => String(a.employee_name || "").localeCompare(String(b.employee_name || "")));
  const phoneOf = (r) => {
    const key = String(r.employee_name || "").trim().toUpperCase();
    return (masters || []).find((m) => String(m.name || "").trim().toUpperCase() === key)?.phone || "";
  };
  const cur = list[idx];
  const reset = () => { setIdx(0); setDone(new Set()); };
  const sendCur = () => {
    if (!cur) return;
    shareSlipWhatsApp(cur, masters);
    setDone((p) => new Set(p).add(cur.id));
    if (idx < list.length - 1) setIdx(idx + 1);
  };
  return (
    <>
      <Button variant="outline" onClick={() => { setOpen(true); reset(); }} data-testid="bulk-slips-btn">
        <MessageCircle className="mr-2 h-4 w-4" /> Bulk Slips
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Bulk Salary Slips — WhatsApp</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <Select value={month} onValueChange={(v) => { setMonth(v); reset(); }}>
                <SelectTrigger className="w-[140px]" data-testid="bulk-slip-month"><SelectValue placeholder="Month" /></SelectTrigger>
                <SelectContent>
                  {(months.length ? months : [month]).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={branch} onValueChange={(v) => { setBranch(v); reset(); }}>
                <SelectTrigger className="w-[140px]" data-testid="bulk-slip-branch"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["All", ...BRANCHES].map((b) => <SelectItem key={b} value={b}>{b === "All" ? "All Branches" : b}</SelectItem>)}
                </SelectContent>
              </Select>
              <span className="text-xs text-muted-foreground">{list.length} slip{list.length === 1 ? "" : "s"}</span>
            </div>
            {list.length === 0 ? (
              <div className="py-6 text-center text-sm text-muted-foreground" data-testid="bulk-slip-empty">No salaries recorded for {month}{branch !== "All" ? ` · ${branch}` : ""}.</div>
            ) : (
              <>
                <div className="rounded-md border bg-slate-50 p-3" data-testid="bulk-slip-current">
                  <div className="mb-1 text-xs font-semibold text-muted-foreground">Slip {Math.min(idx + 1, list.length)} of {list.length}</div>
                  {cur && (
                    <>
                      <div className="text-sm font-bold">{cur.employee_name} <span className="font-normal text-muted-foreground">· {cur.office || "—"} · {cur.designation || "—"}</span></div>
                      <div className="mt-1 text-xs text-muted-foreground">Days {cur.payable_days || "—"} · Basic {inr(cur.basic)} · <b className="text-foreground">Net {inr(cur.net_pay ?? (Number(cur.basic) || 0) + (Number(cur.allowances) || 0) - (Number(cur.deductions) || 0))}</b></div>
                      <div className="mt-1 text-xs">{phoneOf(cur) ? <span className="text-emerald-700">WhatsApp: {phoneOf(cur)}</span> : <span className="font-semibold text-red-600">No phone in Staff Master — will be skipped</span>}</div>
                    </>
                  )}
                  <div className="mt-2 flex flex-wrap gap-2">
                    <Button size="sm" className="bg-emerald-600 text-white hover:bg-emerald-700" disabled={!cur || !phoneOf(cur)} onClick={sendCur} data-testid="bulk-slip-send-btn">
                      <MessageCircle className="mr-1 h-4 w-4" /> Send &amp; Next
                    </Button>
                    <Button size="sm" variant="outline" disabled={idx >= list.length - 1} onClick={() => setIdx(idx + 1)} data-testid="bulk-slip-skip-btn">Skip</Button>
                    <Button size="sm" variant="outline" disabled={idx === 0} onClick={() => setIdx(idx - 1)} data-testid="bulk-slip-back-btn">Back</Button>
                  </div>
                </div>
                <div className="max-h-44 space-y-1 overflow-y-auto">
                  {list.map((r, i) => (
                    <button
                      key={r.id}
                      type="button"
                      onClick={() => setIdx(i)}
                      className={`flex w-full items-center justify-between rounded-md border px-2.5 py-1.5 text-left text-xs ${i === idx ? "border-emerald-400 bg-emerald-50" : "bg-white"}`}
                      data-testid="bulk-slip-row"
                    >
                      <span className="font-medium">{i + 1}. {r.employee_name}</span>
                      <span className={done.has(r.id) ? "font-bold text-emerald-600" : phoneOf(r) ? "text-muted-foreground" : "font-semibold text-red-500"}>
                        {done.has(r.id) ? "✓ Sent" : phoneOf(r) ? inr(r.net_pay ?? 0) : "No phone"}
                      </span>
                    </button>
                  ))}
                </div>
                <div className="text-xs text-muted-foreground">{done.size} of {list.length} sent — WhatsApp opens per staff; press send there, then come back and continue.</div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function MarkPaidBulk({ rows, reload }) {
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [branch, setBranch] = useState("All");
  const [paidDate, setPaidDate] = useState(today());
  const [mode, setMode] = useState("Bank");
  const [busy, setBusy] = useState(false);
  const months = Array.from(new Set((rows || []).map((r) => r.month).filter(Boolean))).sort().reverse();
  const pend = (rows || []).filter((r) => r.month === month && (branch === "All" || (r.office || "") === branch) && r.status !== "Paid");
  const totalNet = pend.reduce((s, r) => s + Number(r.net_pay ?? (Number(r.basic) || 0) + (Number(r.allowances) || 0) - (Number(r.deductions) || 0) - (Number(r.pf) || 0) - (Number(r.esi) || 0)), 0);
  const markAll = async () => {
    if (!pend.length || !paidDate) { toast.error("Pick the paid date"); return; }
    if (!window.confirm(`Mark ${pend.length} slip${pend.length > 1 ? "s" : ""} of ${month}${branch !== "All" ? ` (${branch})` : ""} as PAID on ${fmtDate(paidDate)} via ${mode}?`)) return;
    setBusy(true);
    let ok = 0, fail = 0;
    for (const r of pend) {
      try {
        await api.put(`/salaries/${r.id}`, { ...r, status: "Paid", paid_date: paidDate, payment_mode: mode });
        ok += 1;
      } catch { fail += 1; }
    }
    setBusy(false);
    toast[fail ? "error" : "success"](`${ok} slip${ok === 1 ? "" : "s"} marked Paid${fail ? `, ${fail} failed` : ""}`);
    reload && reload();
    if (!fail) setOpen(false);
  };
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="mark-paid-bulk-btn">
        <CheckCircle2 className="mr-2 h-4 w-4" /> Mark Paid
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Mark Month as Paid</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <Select value={month} onValueChange={setMonth}>
                <SelectTrigger className="w-[130px]" data-testid="mark-paid-month"><SelectValue placeholder="Month" /></SelectTrigger>
                <SelectContent>{(months.length ? months : [month]).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={branch} onValueChange={setBranch}>
                <SelectTrigger className="w-[130px]" data-testid="mark-paid-branch"><SelectValue /></SelectTrigger>
                <SelectContent>{["All", ...BRANCHES].map((b) => <SelectItem key={b} value={b}>{b === "All" ? "All Branches" : b}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Input type="date" className="w-[150px]" value={paidDate} onChange={(e) => setPaidDate(e.target.value)} data-testid="mark-paid-date" />
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger className="w-[140px]" data-testid="mark-paid-mode"><SelectValue /></SelectTrigger>
                <SelectContent>{MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="rounded-md border bg-slate-50 px-3 py-2 text-sm" data-testid="mark-paid-summary">
              {pend.length ? (
                <><b>{pend.length}</b> pending slip{pend.length > 1 ? "s" : ""} · Total <b>{inr(totalNet)}</b>
                  <div className="mt-1 text-xs text-muted-foreground">{pend.map((r) => r.employee_name).join(", ")}</div></>
              ) : (
                <span className="text-muted-foreground">No pending slips for {month}{branch !== "All" ? ` · ${branch}` : ""}.</span>
              )}
            </div>
            <Button className="w-full bg-emerald-600 text-white hover:bg-emerald-700" disabled={!pend.length || busy} onClick={markAll} data-testid="mark-paid-confirm-btn">
              {busy ? "Marking…" : `Mark ${pend.length || ""} slip${pend.length === 1 ? "" : "s"} as Paid`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

const MODES = ["Bank", "Cash", "UPI", "Cheque", "RTGS", "NEFT", "IMPS", "DD"];
import { BRANCHES } from "@/lib/branches";

function monthNow() {
  return new Date().toISOString().slice(0, 7);
}

function AttendanceLink({ form, setForm }) {
  const [rows, setRows] = useState(null);
  const [events, setEvents] = useState([]);
  useEffect(() => {
    api.get("/attendance").then((r) => setRows(r.data || [])).catch(() => setRows([]));
    api.get("/calendar-events").then((r) => setEvents((r.data || []).filter((e) => e.type === "Holiday"))).catch(() => {});
  }, []);
  const name = (form.employee_name || "").trim().toUpperCase();
  const month = (form.month || "").trim();
  const rel = rows && name && /^\d{4}-\d{2}$/.test(month)
    ? rows.filter((r) => (r.staff_name || "").trim().toUpperCase() === name && (r.date || "").startsWith(month))
    : [];
  const c = { Present: 0, "Half Day": 0, Absent: 0, Leave: 0, Holiday: 0 };
  rel.forEach((r) => { c[r.status] !== undefined ? (c[r.status] += 1) : (c.Present += 1); });
  const attDates = new Set(rel.map((r) => (r.date || "").slice(0, 10)));
  const office = (form.office || "").trim();
  const extraHol = /^\d{4}-\d{2}$/.test(month)
    ? [...new Set(events.filter((h) => (h.date || "").startsWith(month) && (!h.branch || h.branch === "All" || !office || h.branch === office)).map((h) => (h.date || "").slice(0, 10)))].filter((d) => !attDates.has(d))
    : [];
  const payable = c.Present + c.Holiday + c["Half Day"] * 0.5 + extraHol.length;
  const autoFillable = rel.length > 0 && !Number(form.payable_days);
  useEffect(() => {
    if (autoFillable) {
      setForm((f) => (Number(f.payable_days) ? f : { ...f, payable_days: payable }));
      toast.success(`Payable days auto-filled from attendance: ${payable}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFillable, payable]);
  if (!rows || !name || !/^\d{4}-\d{2}$/.test(month)) return null;
  if (!rel.length) return (
    <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500" data-testid="attendance-link-empty">
      No attendance records found for {form.employee_name} in {month}.
    </div>
  );
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2" data-testid="attendance-link-panel">
      <span className="text-xs text-emerald-800">
        <b>Attendance {month}:</b> Present {c.Present} · Half Day {c["Half Day"]} · Absent {c.Absent} · Leave {c.Leave} · Holiday {c.Holiday}
        {extraHol.length > 0 && <> · <b className="text-blue-700">Company Holidays +{extraHol.length} (auto-paid)</b></>} → <b>Payable {payable} days</b>
      </span>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-7 border-emerald-300 text-xs text-emerald-700 hover:bg-emerald-100"
        onClick={() => setForm((f) => ({ ...f, payable_days: payable }))}
        data-testid="apply-payable-days-btn"
      >
        Apply {payable} days
      </Button>
    </div>
  );
}

function ProRataPanel({ form, setForm }) {
  const monthly = Number(form.monthly_basic) || 0;
  const days = Number(form.payable_days) || 0;
  const month = (form.month || "").trim();
  const ok = monthly > 0 && days > 0 && /^\d{4}-\d{2}$/.test(month);
  const dim = ok ? new Date(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 0).getDate() : 0;
  const prorata = ok ? Math.round((monthly * Math.min(days, dim)) / dim) : 0;
  const autoFillable = ok && !Number(form.basic);
  useEffect(() => {
    if (autoFillable) {
      setForm((f) => (Number(f.basic) ? f : { ...f, basic: prorata }));
      toast.success(`Pro-rata basic auto-filled: ${inr(prorata)} (${days}/${dim} days)`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFillable, prorata]);
  if (!ok) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-sky-200 bg-sky-50 px-3 py-2" data-testid="prorata-panel">
      <span className="text-xs text-sky-800">
        <b>Pro-rata:</b> {inr(monthly)} × {Math.min(days, dim)}/{dim} days = <b>{inr(prorata)}</b>
      </span>
      {Number(form.basic) !== prorata && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 border-sky-300 text-xs text-sky-700 hover:bg-sky-100"
          onClick={() => setForm((f) => ({ ...f, basic: prorata }))}
          data-testid="apply-prorata-btn"
        >
          Apply {inr(prorata)}
        </Button>
      )}
    </div>
  );
}

function BasicMemory({ form, setForm }) {
  const [prev, setPrev] = useState(null);
  const name = (form.employee_name || "").trim().toUpperCase();
  useEffect(() => {
    if (!name || form.id) { setPrev(null); return; }
    api.get("/salaries").then((r) => {
      const last = (r.data || [])
        .filter((s) => (s.employee_name || "").trim().toUpperCase() === name && (Number(s.monthly_basic) > 0 || Number(s.basic) > 0))
        .sort((a, b) => String(b.month || "").localeCompare(String(a.month || "")))[0];
      setPrev(last || null);
    }).catch(() => setPrev(null));
  }, [name, form.id]);
  const remembered = prev ? Number(prev.monthly_basic) || Number(prev.basic) : 0;
  const fillable = remembered > 0 && !Number(form.monthly_basic);
  useEffect(() => {
    if (fillable) {
      setForm((f) => (Number(f.monthly_basic) ? f : { ...f, monthly_basic: remembered }));
      toast.success(`Monthly basic remembered from ${prev.month}: ${inr(remembered)}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fillable, remembered]);
  if (!prev || !remembered) return null;
  return (
    <div className="rounded-md border border-violet-200 bg-violet-50 px-3 py-2 text-xs text-violet-800" data-testid="basic-memory-panel">
      <b>Last salary ({prev.month}):</b> monthly basic {inr(remembered)}{Number(form.monthly_basic) === remembered ? " — pre-filled" : ""}
    </div>
  );
}

function BankMemory({ form, setForm }) {
  const [prev, setPrev] = useState(null);
  const name = (form.employee_name || "").trim().toUpperCase();
  useEffect(() => {
    if (!name || form.id) { setPrev(null); return; }
    api.get("/salaries").then((r) => {
      const last = (r.data || [])
        .filter((s) => (s.employee_name || "").trim().toUpperCase() === name && (String(s.account_no || "").trim() || String(s.bank_name || "").trim()))
        .sort((a, b) => String(b.month || "").localeCompare(String(a.month || "")))[0];
      setPrev(last || null);
    }).catch(() => setPrev(null));
  }, [name, form.id]);
  useEffect(() => {
    if (!prev) return;
    setForm((f) => {
      if (String(f.account_no || "").trim() || String(f.bank_name || "").trim()) return f;
      toast.success(`Bank details remembered from ${prev.month}`);
      return { ...f, account_no: prev.account_no || "", bank_name: prev.bank_name || "", ifsc_code: prev.ifsc_code || "", bank_address: prev.bank_address || "", employee_code: String(f.employee_code || "").trim() || prev.employee_code || "" };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prev]);
  if (!prev) return null;
  return (
    <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800" data-testid="bank-memory-panel">
      <b>Bank remembered ({prev.month}):</b> A/c {prev.account_no || "—"} · {prev.bank_name || "—"}{prev.ifsc_code ? ` · ${prev.ifsc_code}` : ""}
    </div>
  );
}

function NeftExport({ rows, reload }) {
  const [exported, setExported] = useState([]);
  const [marking, setMarking] = useState(false);
  const net = (s) => s.net_pay ?? (Number(s.basic) || 0) + (Number(s.allowances) || 0) - (Number(s.deductions) || 0) - (Number(s.pf) || 0) - (Number(s.esi) || 0) - (Number(s.loan) || 0);
  const exportNeft = () => {
    const pending = (rows || []).filter((r) => (r.status || "Pending") !== "Paid");
    if (!pending.length) {
      toast.info("No pending salaries to export — everything is paid.");
      return;
    }
    const cell = (v) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const lines = ["Sl,Employee Name,Employee Code,Account No.,Bank Name,IFSC Code,Bank Address,Amount,Month,Branch"];
    pending.forEach((r, i) => lines.push([i + 1, r.employee_name || "", r.employee_code || "", r.account_no || "", r.bank_name || "", r.ifsc_code || "", r.bank_address || "", net(r), r.month || "", r.office || ""].map(cell).join(",")));
    const missing = pending.filter((r) => !String(r.account_no || "").trim() || !String(r.ifsc_code || "").trim()).length;
    const url = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `neft-salary-upload-${today()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setExported(pending.map((r) => r.id));
    toast.success(`NEFT file downloaded — ${pending.length} pending salary(s)${missing ? ` · ${missing} missing A/c or IFSC` : ""}`);
  };
  const markPaid = async () => {
    const list = (rows || []).filter((r) => exported.includes(r.id) && (r.status || "Pending") !== "Paid");
    if (!list.length) { setExported([]); return; }
    setMarking(true);
    try {
      for (const r of list) {
        await api.put(`/salaries/${r.id}`, { ...r, status: "Paid", paid_date: today(), payment_mode: !r.payment_mode || r.payment_mode === "Bank" ? "NEFT" : r.payment_mode });
      }
      toast.success(`${list.length} salary(s) marked Paid via NEFT`);
      setExported([]);
      reload && reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Mark paid failed");
    } finally {
      setMarking(false);
    }
  };
  return (
    <>
      <Button variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={exportNeft} data-testid="neft-export-btn">
        <FileSpreadsheet className="mr-2 h-4 w-4" /> NEFT Export
      </Button>
      {exported.length > 0 && (
        <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={markPaid} disabled={marking} data-testid="neft-mark-paid-btn">
          <CheckCircle2 className="mr-2 h-4 w-4" /> {marking ? "Marking…" : `Mark ${exported.length} Paid`}
        </Button>
      )}
    </>
  );
}

function DuplicateMonthWarn({ form }) {
  const [dup, setDup] = useState(null);
  const name = (form.employee_name || "").trim().toUpperCase();
  const month = (form.month || "").trim();
  useEffect(() => {
    if (!name || !/^\d{4}-\d{2}$/.test(month)) { setDup(null); return; }
    api.get("/salaries").then((r) => {
      const found = (r.data || []).find((s) => s.id !== form.id && (s.employee_name || "").trim().toUpperCase() === name && (s.month || "").trim() === month);
      setDup(found || null);
    }).catch(() => setDup(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, month, form.id]);
  if (!dup) return null;
  return (
    <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700" data-testid="duplicate-month-warn">
      ⚠ {dup.employee_name} already has a salary entry for {month} (Ref {String(dup.salary_no || "").replace(/^SAL[-\s]?/i, "") || "—"} · Net {inr(dup.net_pay)} · {dup.status || "Pending"}). Saving will create a duplicate.
    </div>
  );
}

function BranchSalaryChips({ rows }) {
  const months = Array.from(new Set((rows || []).map((r) => r.month).filter(Boolean))).sort().reverse();
  const [month, setMonth] = useState("");
  const m = month || months[0] || "";
  if (!months.length) return null;
  const net = (s) => Number(s.net_pay ?? (Number(s.basic) || 0) + (Number(s.allowances) || 0) - (Number(s.deductions) || 0) - (Number(s.pf) || 0) - (Number(s.esi) || 0) - (Number(s.loan) || 0)) || 0;
  const list = (rows || []).filter((r) => r.month === m);
  const totals = {};
  list.forEach((r) => { const b = r.office || "No Branch"; totals[b] = (totals[b] || 0) + net(r); });
  const grand = list.reduce((a, r) => a + net(r), 0);
  return (
    <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="branch-salary-chips">
      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Salary by branch:</span>
      <Select value={m} onValueChange={setMonth}>
        <SelectTrigger className="h-7 w-[110px] text-xs" data-testid="branch-salary-month"><SelectValue /></SelectTrigger>
        <SelectContent>{months.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
      </Select>
      {Object.entries(totals).sort((a, b) => b[1] - a[1]).map(([b, t]) => (
        <span key={b} className="rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-semibold text-indigo-700" data-testid="branch-salary-chip">
          {b} <b>{inr(t)}</b>
        </span>
      ))}
      <span className="rounded-full bg-slate-800 px-2.5 py-1 text-xs font-semibold text-white" data-testid="branch-salary-total-chip">
        Total {inr(grand)} · {list.length} staff
      </span>
    </div>
  );
}

function CodeAutofill({ form, setForm, masters }) {
  const name = String(form.employee_name || "").trim().toLowerCase();
  useEffect(() => {
    if (!name || String(form.employee_code || "").trim()) return;
    const m = (masters || []).find((s) => String(s.name || "").trim().toLowerCase() === name);
    if (m && m.staff_code) setForm((f) => (String(f.employee_code || "").trim() ? f : { ...f, employee_code: m.staff_code }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, masters]);
  return null;
}

export default function StaffSalary() {
  const [masters, setMasters] = useState([]);
  useEffect(() => {
    api.get("/staff-master").then((r) => setMasters((r.data || []).filter((m) => (m.status || "Active") !== "Inactive"))).catch(() => {});
  }, []);
  const employeeField = { key: "employee_name", label: "Employee Name", type: "text", placeholder: "e.g. RAMESH KUMAR" };
  return (
    <EntityManager
      title="NTC Staff Salary"
      subtitle="Record monthly payroll for drivers and staff. Net pay = basic + allowances − deductions."
      endpoint="salaries"
      entityName="Salary"
      defaults={{
        month: monthNow(),
        employee_name: "",
        employee_code: "",
        designation: "",
        office: "Chennai",
        payable_days: 0,
        monthly_basic: 0,
        basic: 0,
        allowances: 0,
        deductions: 0,
        pf: 0,
        esi: 0,
        loan: 0,
        payment_mode: "Bank",
        cheque_no: "",
        cheque_date: "",
        cheque_amount: 0,
        bank_name: "",
        account_no: "",
        ifsc_code: "",
        bank_address: "",
        status: "Pending",
        paid_date: today(),
        remarks: "",
      }}
      fields={[
        employeeField,
        { key: "employee_code", label: "Employee Code", type: "text", placeholder: "e.g. NTC 002" },
        { key: "designation", label: "Designation", type: "text", placeholder: "e.g. Senior Driver" },
        { key: "office", label: "Branch", type: "select", options: BRANCHES },
        { key: "month", label: "Salary Month (YYYY-MM)", type: "text", placeholder: "2026-08" },
        { key: "payable_days", label: "Payable days (from attendance)", type: "number", placeholder: "26" },
        { key: "monthly_basic", label: "Monthly basic — full month (₹)", type: "number", placeholder: "e.g. 22000 → pro-rata fills Basic pay" },
        { key: "basic", label: "Basic pay (₹)", type: "number", placeholder: "22000" },
        { key: "allowances", label: "Allowances (₹)", type: "number", placeholder: "3000" },
        { key: "deductions", label: "Deductions (₹)", type: "number", placeholder: "1000" },
        { key: "pf", label: "PF (₹)", type: "number", placeholder: "1800" },
        { key: "esi", label: "ESI (₹)", type: "number", placeholder: "500" },
        { key: "loan", label: "Loan (₹)", type: "number", placeholder: "0" },
        { key: "payment_mode", label: "Payment Mode", type: "select", options: MODES },
        { key: "cheque_no", label: "Cheque No.", type: "text", placeholder: "e.g. 123456", showIf: (f) => f.payment_mode === "Cheque" },
        { key: "cheque_date", label: "Cheque Date", type: "date", showIf: (f) => f.payment_mode === "Cheque" },
        { key: "cheque_amount", label: "Cheque Amount (₹)", type: "number", placeholder: "25000", showIf: (f) => f.payment_mode === "Cheque" },
        { key: "account_no", label: "Account No.", type: "text", placeholder: "1234567890" },
        { key: "bank_name", label: "Bank Name", type: "text", placeholder: "Punjab National Bank" },
        { key: "ifsc_code", label: "IFSC Code", type: "text", placeholder: "PUNB0123456" },
        { key: "bank_address", label: "Bank Address", type: "text", placeholder: "Branch address of the bank" },
        { key: "status", label: "Status", type: "select", options: ["Pending", "Paid"] },
        { key: "paid_date", label: "Paid date", type: "text", placeholder: "YYYY-MM-DD" },
        { key: "remarks", label: "Remarks", type: "textarea", placeholder: "Notes" },
      ]}
      columns={[
        { key: "_sl", label: "SL No.", render: (r, i) => i + 1 },
        { key: "office", label: "Branch", render: (r) => r.office || "—" },
        { key: "employee_name", label: "Employee Name" },
        { key: "employee_code", label: "Employee Code", render: (r) => r.employee_code || "—" },
        { key: "designation", label: "Designation", render: (r) => r.designation || "—" },
        { key: "month", label: "Salary Month" },
        { key: "salary_no", label: "Ref No.", render: (r) => String(r.salary_no || "").replace(/^SAL[-\s]?/i, "") || "—" },
        { key: "payable_days", label: "Days", align: "right", render: (r) => (r.payable_days ? r.payable_days : "—") },
        { key: "basic", label: "Basic", align: "right", render: (r) => inr(r.basic) },
        { key: "pf", label: "PF", align: "right", render: (r) => inr(r.pf) },
        { key: "esi", label: "ESI", align: "right", render: (r) => inr(r.esi) },
        { key: "loan", label: "Loan", align: "right", render: (r) => inr(r.loan) },
        { key: "net_pay", label: "Net Pay", align: "right", render: (r) => inr(r.net_pay) },
        { key: "account_no", label: "Account No.", render: (r) => r.account_no || "—" },
        { key: "bank_name", label: "Bank Name", render: (r) => r.bank_name || "—" },
        { key: "ifsc_code", label: "IFSC Code", render: (r) => r.ifsc_code || "—" },
        { key: "bank_address", label: "Bank Address", render: (r) => r.bank_address || "—" },
        { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
      ]}
      selectable
      onPrintSelected={(list) => printSalaryRegister(list, "All")}
      headerExtra={(rows, _d, _f, load) => (
        <>
          <SalaryRegister rows={rows} />
          <NeftExport rows={rows} reload={load} />
          <BulkSlipSender rows={rows} masters={masters} />
          <MarkPaidBulk rows={rows} reload={load} />
        </>
      )}
      banner={(rows) => <BranchSalaryChips rows={rows} />}
      formExtra={(form, setForm) => (
        <>
          <CodeAutofill form={form} setForm={setForm} masters={masters} />
          <DuplicateMonthWarn form={form} />
          <AttendanceLink form={form} setForm={setForm} />
          <BasicMemory form={form} setForm={setForm} />
          <BankMemory form={form} setForm={setForm} />
          <ProRataPanel form={form} setForm={setForm} />
        </>
      )}
      formExtraAfter="monthly_basic"
      rowActions={(row) => (
        <>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-primary"
            title="Print salary slip"
            onClick={() => printSalarySlip(row)}
            data-testid="print-salary-btn"
          >
            <Printer className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-emerald-600"
            title="Share salary slip on WhatsApp"
            onClick={() => shareSlipWhatsApp(row, masters)}
            data-testid="share-salary-whatsapp-btn"
          >
            <MessageCircle className="h-4 w-4" />
          </Button>
        </>
      )}
    />
  );
}
